import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Settings, User, LogOut, Menu, X, Headset, LayoutDashboard, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import "./topBar.css";
import BASE_URL from "../../config";
import AccessibilityControl from '../accessibility/AccessibilityControl';

const TopBar = ({ isSidebarCollapsed = false, onToggleSidebar }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserProfile = async () => {
      const token = localStorage.getItem("jwt_token");
      if (token) {
        try {
          const response = await fetch(`${BASE_URL}/api/user-profile/`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          if (response.ok) {
            const userData = await response.json();
            setIsAdmin(userData.is_admin || false);
          }
        } catch (error) {
          console.error("Failed to fetch user profile:", error);
        } finally {
          setProfileLoaded(true);
        }
      } else {
        setProfileLoaded(true);
      }
    };

    fetchUserProfile();
  }, []);

  // The Messages bell is a customer-portal feature -- staff chat with
  // customers from Helpdesk's own inbox instead, so this only polls for
  // non-admin accounts. Polling (not the chat WebSocket) is deliberate
  // here: the badge just needs to be roughly current, and every page in
  // the app renders TopBar, so this avoids holding open a WS connection
  // app-wide just for a number in the corner.
  useEffect(() => {
    if (!profileLoaded || isAdmin) return;

    const fetchUnread = () => {
      const token = localStorage.getItem("jwt_token");
      if (!token) return;
      fetch(`${BASE_URL}/api/chat/unread-count/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (data) setUnreadMessages(data.unread_count || 0);
        })
        .catch(() => {});
    };

    fetchUnread();
    const interval = setInterval(fetchUnread, 15000);
    return () => clearInterval(interval);
  }, [profileLoaded, isAdmin]);

  const handleMessagesClick = () => {
    navigate("/user-dashboard/messages");
  };

  const handleLogout = () => {
    localStorage.removeItem("jwt_token");
    navigate("/signin");
  };

  const handleSettingsClick = () => {
    navigate("/user-dashboard/settings");
    setDropdownOpen(false);
  };

  const handleProfileClick = () => {
    navigate("/user-dashboard/edit-profile");
    setDropdownOpen(false);
  };

  const handleControlPanelClick = () => {
    window.location.href = "http://20.117.120.208:8000/admin";
  };

  const handleSupportClick = () => {
    navigate("/user-dashboard/tickets");
  };

  const closeDropdown = () => {
    setDropdownOpen(false);
  };

  const handleKeyDown = (e, handler) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handler();
    }
  };

  return (
    <>
      {/* Dropdown Overlay */}
      {dropdownOpen && (
        <div className="topbar-overlay" onClick={closeDropdown} aria-hidden="true"></div>
      )}

      <header className="aws-topbar" role="banner">
        <div className="topbar-content">
          {/* Left Section - Sidebar toggle + Welcome Message */}
          <div className="topbar-left">
            {onToggleSidebar && (
              <button
                className="topbar-icon-btn sidebar-toggle-btn"
                onClick={onToggleSidebar}
                aria-label={isSidebarCollapsed ? "Show navigation panel" : "Hide navigation panel"}
                aria-pressed={isSidebarCollapsed}
                title={isSidebarCollapsed ? "Show navigation panel" : "Hide navigation panel"}
              >
                {isSidebarCollapsed ? <PanelLeftOpen size={20} aria-hidden="true" /> : <PanelLeftClose size={20} aria-hidden="true" />}
              </button>
            )}
            <div className="topbar-welcome">
              <h1 className="welcome-title">Console Home</h1>
              <span className="welcome-breadcrumb">Management Dashboard</span>
            </div>
          </div>

          {/* Right Section - Actions */}
          <div className="topbar-right">
            {/* Messages / Notifications */}
            {!isAdmin && (
              <button
                className="topbar-icon-btn"
                aria-label={
                  unreadMessages > 0
                    ? `Messages, ${unreadMessages} unread`
                    : "Messages, no unread messages"
                }
                onClick={handleMessagesClick}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                </svg>
                {unreadMessages > 0 && (
                  <span className="notification-badge" aria-hidden="true">
                    {unreadMessages > 9 ? "9+" : unreadMessages}
                  </span>
                )}
              </button>
            )}

            <AccessibilityControl />

            {/* Support Tickets */}
            <button
              className="topbar-icon-btn"
              aria-label="Support Tickets"
              onClick={handleSupportClick}
            >
              <Headset size={20} aria-hidden="true" />
            </button>

            {/* Settings Dropdown */}
            <div className="topbar-dropdown-container">
              <button
                className="topbar-settings-btn"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                aria-label="Settings menu"
                aria-expanded={dropdownOpen}
                aria-haspopup="true"
              >
                <Settings size={20} aria-hidden="true" />
                <span className="settings-text">Settings</span>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className={`dropdown-arrow ${dropdownOpen ? 'open' : ''}`}
                  aria-hidden="true"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>

              {/* Dropdown Menu */}
              {dropdownOpen && (
                <div className="topbar-dropdown-menu" role="menu" aria-label="Settings">
                  <div className="dropdown-header">
                    <span className="dropdown-title">Account</span>
                  </div>

                  <ul className="dropdown-list" role="none">
                    <li
                      className="dropdown-item"
                      role="menuitem"
                      tabIndex={0}
                      onClick={handleProfileClick}
                      onKeyDown={(e) => handleKeyDown(e, handleProfileClick)}
                    >
                      <User size={18} aria-hidden="true" />
                      <span>Profile</span>
                    </li>

                    {isAdmin && (
                      <li
                        className="dropdown-item"
                        role="menuitem"
                        tabIndex={0}
                        onClick={handleControlPanelClick}
                        onKeyDown={(e) => handleKeyDown(e, handleControlPanelClick)}
                      >
                        <LayoutDashboard size={18} aria-hidden="true" />
                        <span>Control Panel</span>
                      </li>
                    )}

                    {!isAdmin && (
                      <li
                        className="dropdown-item"
                        role="menuitem"
                        tabIndex={0}
                        onClick={handleSettingsClick}
                        onKeyDown={(e) => handleKeyDown(e, handleSettingsClick)}
                      >
                        <Settings size={18} aria-hidden="true" />
                        <span>Settings</span>
                      </li>
                    )}
                  </ul>

                  <div className="dropdown-divider" role="separator"></div>

                  <ul className="dropdown-list" role="none">
                    <li
                      className="dropdown-item logout"
                      role="menuitem"
                      tabIndex={0}
                      onClick={handleLogout}
                      onKeyDown={(e) => handleKeyDown(e, handleLogout)}
                    >
                      <LogOut size={18} aria-hidden="true" />
                      <span>Sign Out</span>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default TopBar;